<script>
import "@vueform/multiselect/themes/default.scss";
import "flatpickr/dist/flatpickr.css";
import Layout from "../../layouts/main.vue";
import calendar from "../../components/appoinmentCalendar";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import useVuelidate from "@vuelidate/core";
import axios from "axios";
import jwt_decode from "jwt-decode";

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Monday', tuesday:'Tuesday', wednesday:'Wednesday', thursday:'Thursday', friday:'Friday', saturday:'Saturday', sunday:'Sunday' };

export default {
  page: {
    title: "Agents Calendar",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader, calendar },
  data() {
    return {
      title: "Agents calendar",
      items: [
        { text: "Calendar", href: "/" },
        { text: "Agents", active: true },
      ],
      timeConfig: { enableTime: false, dateFormat: "d M, Y" },
      dateTimeConfig: { enableTime: true, dateFormat: "m-d-y H:i K" },

      // Schedule config modal
      showScheduleModal: false,
      scheduleLoading: false,
      scheduleSaving: false,
      scheduleSaved: false,
      scheduleConfig: Object.fromEntries(DAYS.map(d => [d, []])),
      newSlot: Object.fromEntries(DAYS.map(d => [d, ''])),
      scheduleDeptId: null,
      isDarkMode: false,
      meetingLink: '',
      meetingPassword: '',
      availableDepts: [],
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    days() { return DAYS; },
    dayLabels() { return DAY_LABELS; },
  },
  watch: {
    showScheduleModal(val) {
      if (!val) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');
      }
    },
  },
  methods: {
    emptySchedule() {
      return Object.fromEntries(DAYS.map(d => [d, []]));
    },

    jwtPayload() {
      try { return jwt_decode(localStorage.getItem('jwt')); }
      catch (e) { return {}; }
    },

    openScheduleConfig() {
      // Open modal immediately — load data after
      this.showScheduleModal = true;
      this.scheduleLoading = true;
      this.scheduleConfig = Object.fromEntries(DAYS.map(d => [d, []]));

      const payload = this.jwtPayload();
      let depts = [];
      try {
        const raw = payload.sAccss;
        const parsed = (raw && raw !== '') ? JSON.parse(raw) : null;
        if (Array.isArray(parsed) && parsed.length > 0) {
          depts = parsed.map(Number).filter(Boolean);
        }
      } catch (e) { /* ignore */ }
      if (depts.length === 0) depts = [Number(payload.departmentID)].filter(Boolean);
      this.availableDepts = depts;
      this.scheduleDeptId = depts[0] || null;

      this.loadSchedule();
    },

    loadSchedule() {
      if (!this.scheduleDeptId) {
        this.scheduleLoading = false;
        return;
      }
      this.scheduleLoading = true;
      axios.get('/api/department-schedule', {
        params: { token: localStorage.getItem('jwt'), departmentID: this.scheduleDeptId },
      }).then(res => {
        this.scheduleConfig = res.data.schedule || Object.fromEntries(DAYS.map(d => [d, []]));
        this.meetingLink = res.data.meetingLink || '';
        this.meetingPassword = res.data.meetingPassword || '';
      }).catch(() => {
        this.scheduleConfig = Object.fromEntries(DAYS.map(d => [d, []]));
        this.meetingLink = '';
        this.meetingPassword = '';
      }).finally(() => {
        this.scheduleLoading = false;
      });
    },

    addSlot(day) {
      const val = (this.newSlot[day] || '').trim();
      if (!val || val.length !== 5 || val[2] !== ':') return;
      if (!this.scheduleConfig[day].includes(val)) {
        this.scheduleConfig[day] = [...this.scheduleConfig[day], val].sort();
      }
      this.newSlot[day] = '';
    },

    removeSlot(day, index) {
      this.scheduleConfig[day].splice(index, 1);
    },

    formatSlot(time) {
      const [h, m] = time.split(':').map(Number);
      const p = h < 12 ? 'AM' : 'PM';
      const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return `${h12}:${String(m).padStart(2,'0')} ${p}`;
    },

    async saveSchedule() {
      this.scheduleSaving = true;
      try {
        await axios.post('/api/department-schedule/save', {
          token: localStorage.getItem('jwt'),
          departmentID: this.scheduleDeptId,
          schedule: this.scheduleConfig,
          meetingLink: this.meetingLink,
          meetingPassword: this.meetingPassword,
        });
        this.scheduleSaved = true;
        setTimeout(() => { this.scheduleSaved = false; }, 3000);
      } catch (e) {
        alert('Error saving schedule. Please try again.');
      } finally {
        this.scheduleSaving = false;
      }
    },
  },
  validations: {},
};
</script>

<style scoped>
.slot-tag {
  display: inline-flex; align-items: center; gap: 4px;
  background: var(--vz-success-bg-subtle, #d1e7dd); color: var(--vz-success, #0f5132);
  border: 1px solid var(--vz-success-border-subtle, #a3cfbb);
  border-radius: 4px; padding: 2px 8px;
  font-size: 0.78rem; font-weight: 600;
}
.slot-tag .remove-btn { background: none; border: none; color: inherit; padding: 0 0 0 2px; line-height: 1; cursor: pointer; opacity: .7; }
.slot-tag .remove-btn:hover { opacity: 1; color: #dc3545; }
</style>

<template>
  <Layout>
    <PageHeader :title="title" :items="items"/>

    <div class="d-flex justify-content-end mb-3">
      <button type="button" class="btn btn-soft-success" @click="openScheduleConfig">
        <i class="ri-settings-3-line align-bottom me-1"></i> Schedule Config
      </button>
    </div>

    <calendar></calendar>

    <!-- Schedule Config Modal -->
    <b-modal v-model="showScheduleModal" size="lg" title-class="fw-semibold" modal-class="custom-dark-modal" scrollable>
      <template #title>
        <i class="ri-calendar-settings-line align-bottom me-1"></i> Interview Schedule Config
      </template>

      <!-- Department selector (only if user has multiple) -->
      <div v-if="availableDepts.length > 1" class="mb-3">
        <label class="form-label">Department</label>
        <select class="form-select" style="max-width:220px;" v-model.number="scheduleDeptId" @change="loadSchedule">
          <option v-for="dId in availableDepts" :key="dId" :value="dId">Dept #{{ dId }}</option>
        </select>
      </div>

      <div v-if="scheduleLoading" class="text-center py-4">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else>
        <!-- Meeting Info -->
        <div class="row mb-3 g-3">
          <div class="col-md-7">
            <label class="form-label fs-12 text-muted text-uppercase mb-1"><i class="ri-link me-1"></i> Interview Meeting Link</label>
            <input type="text" class="form-control" v-model="meetingLink" placeholder="e.g., https://zoom.us/j/... or Google Meet" />
          </div>
          <div class="col-md-5">
            <label class="form-label fs-12 text-muted text-uppercase mb-1"><i class="ri-lock-line me-1"></i> Password / PIN</label>
            <input type="text" class="form-control" v-model="meetingPassword" placeholder="Optional passcode" />
          </div>
        </div>

        <div class="row g-3">
          <div v-for="day in days" :key="day" class="col-lg-6">
          <div class="card mb-0 border">
            <div class="card-header bg-light p-2 d-flex align-items-center justify-content-between">
              <span class="fw-semibold text-uppercase fs-12">{{ dayLabels[day] }}</span>
              <span v-if="scheduleConfig[day] && scheduleConfig[day].length"
                    class="badge bg-success-subtle text-success">
                {{ scheduleConfig[day].length }} slot{{ scheduleConfig[day].length > 1 ? 's' : '' }}
              </span>
              <span v-else class="badge bg-light text-muted border">Off</span>
            </div>
            <div class="card-body p-2">
              <div class="d-flex flex-wrap gap-1 mb-2" v-if="scheduleConfig[day] && scheduleConfig[day].length">
                <span v-for="(slot, idx) in scheduleConfig[day]" :key="slot" class="slot-tag">
                  {{ formatSlot(slot) }}
                  <button class="remove-btn" type="button" @click="removeSlot(day, idx)" title="Remove">×</button>
                </span>
              </div>
              <p v-else class="text-muted fs-12 mb-2">No slots — day will be hidden in the calendar.</p>
              <div class="input-group input-group-sm">
                <input type="time" class="form-control" v-model="newSlot[day]" @keyup.enter="addSlot(day)" />
                <button class="btn btn-soft-success" type="button" @click="addSlot(day)" :disabled="!newSlot[day]">
                  <i class="ri-add-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <template #footer>
        <div class="d-flex gap-2 justify-content-end w-100">
          <button type="button" class="btn btn-light" @click="showScheduleModal = false">Close</button>
          <button type="button" class="btn btn-success" @click="saveSchedule" :disabled="scheduleSaving">
            <i v-if="scheduleSaving" class="ri-loader-4-line align-bottom me-1"></i>
            <i v-else-if="scheduleSaved" class="ri-check-line align-bottom me-1"></i>
            <i v-else class="ri-save-line align-bottom me-1"></i>
            {{ scheduleSaving ? 'Saving...' : scheduleSaved ? 'Saved!' : 'Save' }}
          </button>
        </div>
      </template>
    </b-modal>
    <!-- end modal -->

  </Layout>
</template>
<style>
[data-layout-mode="dark"] .custom-dark-modal .modal-content {
  background-color: var(--vz-modal-bg, #212529) !important;
  color: var(--vz-body-color, #ced4da) !important;
  border: 1px solid var(--vz-border-color, #32383e) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
}
[data-layout-mode="dark"] .custom-dark-modal .modal-header {
  background-color: var(--vz-modal-bg, #212529) !important;
  border-bottom: 1px solid var(--vz-border-color, #32383e) !important;
}
[data-layout-mode="dark"] .custom-dark-modal .modal-body {
  background-color: var(--vz-modal-bg, #212529) !important;
}
[data-layout-mode="dark"] .custom-dark-modal .modal-footer {
  background-color: var(--vz-modal-bg, #212529) !important;
  border-top: 1px solid var(--vz-border-color, #32383e) !important;
}
[data-layout-mode="dark"] .custom-dark-modal .modal-title {
  color: var(--vz-heading-color, #f3f6f9) !important;
}
[data-layout-mode="dark"] .custom-dark-modal .btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>
